import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { FaCheckCircle, FaDownload, FaHome, FaCalendarAlt, FaClock, FaMapMarkerAlt, FaInfoCircle, FaSpinner } from 'react-icons/fa';
import QRCode from 'react-qr-code';
import html2canvas from 'html2canvas';
import { db } from '../../../src/admin/servicios/firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';

export default function Confirmacion() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('cargando'); // cargando | exito | fallo
  const [ticketData, setTicketData] = useState(null);
  const [intentos, setIntentos] = useState(0);

  const ticketRef = useRef(null);
  const reversoRef = useRef(null);
  const buscando = useRef(false);

  useEffect(() => {
    const paymentIntent = searchParams.get('payment_intent');
    const redirectStatus = searchParams.get('redirect_status');

    if (redirectStatus !== 'succeeded' || !paymentIntent) {
      setStatus('fallo');
      return;
    }

    if (buscando.current) return;
    buscando.current = true;

    buscarTicket(paymentIntent, 0);
  }, [searchParams]);

  // Busca el ticket creado por el webhook — reintenta hasta 10 veces con 2s de espera
  const buscarTicket = async (paymentIntent, intento) => {
    if (intento >= 10) {
      setStatus('fallo');
      return;
    }

    setIntentos(intento + 1);

    try {
      const ticketsRef = collection(db, 'tickets');

      // Buscar con ambos campos por compatibilidad (webhook usa paymentIntentId)
      const [snap1, snap2] = await Promise.all([
        getDocs(query(ticketsRef, where('idPagoStripe', '==', paymentIntent))),
        getDocs(query(ticketsRef, where('paymentIntentId', '==', paymentIntent))),
      ]);

      const docSnap = !snap1.empty ? snap1.docs[0] : !snap2.empty ? snap2.docs[0] : null;

      if (docSnap) {
        const data = { id: docSnap.id, ...docSnap.data() };
        setTicketData({
          ...data,
          esRedondo: data.tipoViaje === 'redondo',
        });
        setStatus('exito');
        localStorage.removeItem('viajeSeleccionado');
        localStorage.removeItem('detallesCompra');
        localStorage.removeItem('pendingPaymentId');
      } else {
        // Webhook aún no procesó — esperar 2s y reintentar
        setTimeout(() => buscarTicket(paymentIntent, intento + 1), 2000);
      }
    } catch (err) {
      console.error('Error buscando ticket:', err);
      setTimeout(() => buscarTicket(paymentIntent, intento + 1), 2000);
    }
  };

  const descargarImagenes = async () => {
    if (ticketRef.current && reversoRef.current) {
      try {
        const canvasFrente = await html2canvas(ticketRef.current, { scale: 2, useCORS: true });
        const linkFrente = document.createElement('a');
        linkFrente.download = `Boleto_${ticketData.id.slice(0, 5)}_FRENTE.png`;
        linkFrente.href = canvasFrente.toDataURL();
        linkFrente.click();

        await new Promise(resolve => setTimeout(resolve, 500));

        const canvasReverso = await html2canvas(reversoRef.current, { scale: 2, useCORS: true });
        const linkReverso = document.createElement('a');
        linkReverso.download = `Boleto_${ticketData.id.slice(0, 5)}_TERMINOS.png`;
        linkReverso.href = canvasReverso.toDataURL();
        linkReverso.click();
      } catch (error) {
        console.error('Error al descargar:', error);
        alert('Hubo un error al generar las imágenes.');
      }
    }
  };

  // ─── CARGANDO ─────────────────────────────────────────────────────────────
  if (status === 'cargando') return (
    <div className="h-screen flex flex-col items-center justify-center gap-4 bg-slate-900 text-white">
      <FaSpinner className="text-4xl text-blue-400 animate-spin" />
      <p className="font-bold text-lg">Generando tu boleto...</p>
      {intentos > 1 && (
        <p className="text-sm text-slate-400">
          Verificando pago... ({intentos}/10)
        </p>
      )}
    </div>
  );

  // ─── FALLO ────────────────────────────────────────────────────────────────
  if (status === 'fallo') return (
    <div className="h-screen flex flex-col items-center justify-center gap-4 bg-slate-900 text-white text-center px-6">
      <p className="text-2xl font-bold">Error al cargar el boleto</p>
      <p className="text-slate-400 text-sm">
        Si tu pago fue realizado, tu boleto llegará pronto.<br />
        También puedes buscarlo en <strong>Mis Viajes</strong> con tu número de WhatsApp.
      </p>
      <p className="text-slate-400 text-sm">Soporte: 951 611 0942</p>
      <button
        onClick={() => navigate('/mis-viajes')}
        className="mt-4 bg-blue-600 hover:bg-blue-700 text-white py-3 px-6 rounded-xl font-bold"
      >
        Ir a Mis Viajes
      </button>
    </div>
  );

  // ─── ÉXITO ────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-slate-900 p-4 flex flex-col items-center">
      <div className="w-full max-w-sm flex flex-col gap-6 pb-10">

        <h1 className="text-white text-2xl font-bold flex items-center gap-2 justify-center mt-6">
          <FaCheckCircle className="text-green-400" /> ¡Reserva Confirmada!
        </h1>

        {/* BOLETO FRENTE */}
        <div ref={ticketRef} className="bg-white rounded-3xl overflow-hidden shadow-2xl relative">

          <div className={`p-6 text-white text-center relative ${ticketData.esRedondo ? 'bg-gradient-to-r from-blue-600 to-purple-600' : 'bg-blue-600'}`}>
            <div className="text-1xl font-black tracking-widest uppercase">Transportes Ángeles del Puerto</div>
            <p className="text-blue-200 text-sm">
              {ticketData.esRedondo ? 'Boleto Digital Redondo' : 'Boleto Digital'}
            </p>
            <div className="absolute -bottom-3 -left-3 w-6 h-6 bg-slate-900 rounded-full"></div>
            <div className="absolute -bottom-3 -right-3 w-6 h-6 bg-slate-900 rounded-full"></div>
          </div>

          <div className="p-6 pt-8 space-y-4">
            {ticketData.esRedondo ? (
              <>
                <div className="bg-blue-50 p-4 rounded-xl border-l-4 border-blue-500">
                  <p className="text-xs text-blue-600 font-black uppercase mb-2">🔵 Viaje de Ida</p>
                  <div className="flex justify-between items-center mb-2">
                    <div className="text-center">
                      <p className="text-[10px] text-gray-400 font-bold uppercase">Origen</p>
                      <p className="font-bold text-slate-800 text-sm">{ticketData.rutaIda?.origen}</p>
                    </div>
                    <div className="text-gray-300">➔</div>
                    <div className="text-center">
                      <p className="text-[10px] text-gray-400 font-bold uppercase">Destino</p>
                      <p className="font-bold text-slate-800 text-sm">{ticketData.rutaIda?.destino}</p>
                    </div>
                  </div>
                  <div className="flex justify-between text-xs">
                    <div className="flex items-center gap-1">
                      <FaCalendarAlt className="text-blue-500" /><span className="font-bold">{ticketData.fechaIda}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <FaClock className="text-blue-500" /><span className="font-bold">{ticketData.horaIda}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-purple-50 p-4 rounded-xl border-l-4 border-purple-500">
                  <p className="text-xs text-purple-600 font-black uppercase mb-2">🟣 Viaje de Regreso</p>
                  <div className="flex justify-between items-center mb-2">
                    <div className="text-center">
                      <p className="text-[10px] text-gray-400 font-bold uppercase">Origen</p>
                      <p className="font-bold text-slate-800 text-sm">{ticketData.rutaRegreso?.origen}</p>
                    </div>
                    <div className="text-gray-300">➔</div>
                    <div className="text-center">
                      <p className="text-[10px] text-gray-400 font-bold uppercase">Destino</p>
                      <p className="font-bold text-slate-800 text-sm">{ticketData.rutaRegreso?.destino}</p>
                    </div>
                  </div>
                  <div className="flex justify-between text-xs">
                    <div className="flex items-center gap-1">
                      <FaCalendarAlt className="text-purple-500" /><span className="font-bold">{ticketData.fechaRegreso}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <FaClock className="text-purple-500" /><span className="font-bold">{ticketData.horaRegreso}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-50 p-3 rounded-xl">
                  <p className="text-[10px] text-gray-400 font-bold uppercase mb-2">Pasajeros y Asientos</p>
                  {(ticketData.asientosIda || []).map((asientoIda, index) => {
                    const asientoRegreso = (ticketData.asientosRegreso || [])[index];
                    return (
                      <div key={asientoIda} className="flex justify-between text-sm mb-2 border-b border-gray-100 last:border-0 pb-2">
                        <span className="font-bold text-slate-700 flex-1">{ticketData.pasajeros?.[asientoIda]}</span>
                        <div className="flex gap-1">
                          <span className="bg-blue-100 text-blue-700 px-2 rounded font-bold text-xs">Ida: {asientoIda.replace('a', '')}</span>
                          {asientoRegreso && <span className="bg-purple-100 text-purple-700 px-2 rounded font-bold text-xs">Reg: {asientoRegreso.replace('a', '')}</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <>
                <div className="flex justify-between items-center border-b border-dashed border-gray-200 pb-4">
                  <div className="text-center">
                    <p className="text-[10px] text-gray-400 font-bold uppercase">Origen</p>
                    <p className="font-bold text-slate-800 leading-tight">{ticketData.ruta?.origen}</p>
                  </div>
                  <div className="text-gray-300">➔</div>
                  <div className="text-center">
                    <p className="text-[10px] text-gray-400 font-bold uppercase">Destino</p>
                    <p className="font-bold text-slate-800 leading-tight">{ticketData.ruta?.destino}</p>
                  </div>
                </div>

                <div className="flex justify-between bg-slate-50 p-3 rounded-xl">
                  <div className="flex items-center gap-2">
                    <FaCalendarAlt className="text-blue-500" />
                    <div>
                      <p className="text-[10px] text-gray-400 font-bold uppercase">Fecha</p>
                      <p className="font-bold text-sm">{ticketData.fechaViaje}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <FaClock className="text-blue-500" />
                    <div>
                      <p className="text-[10px] text-gray-400 font-bold uppercase">Hora</p>
                      <p className="font-bold text-sm">{ticketData.horaViaje}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-50 p-3 rounded-xl">
                  <p className="text-[10px] text-gray-400 font-bold uppercase mb-2">Pasajeros y Asientos</p>
                  {(ticketData.asientos || ticketData.asientosIda || []).map((asiento) => (
                    <div key={asiento} className="flex justify-between text-sm mb-1 border-b border-gray-100 last:border-0 pb-1">
                      <span className="font-bold text-slate-700">{ticketData.pasajeros?.[asiento]}</span>
                      <span className="bg-blue-100 text-blue-700 px-2 rounded font-bold">Asiento {asiento.replace('a', '')}</span>
                    </div>
                  ))}
                </div>
              </>
            )}

            <div className="flex flex-col items-center justify-center pt-2">
              <div className="p-2 border-2 border-slate-900 rounded-lg">
                <QRCode value={ticketData.id} size={128} level={"H"} />
              </div>
              <p className="text-[10px] text-gray-400 mt-1">ID: {ticketData.id}</p>
              <p className="text-[10px] text-center text-red-400 font-bold mt-1">Muestra este código al abordar</p>
            </div>
          </div>

          <div className="bg-slate-100 p-3 text-center">
            <p className="text-[10px] text-gray-500">
              Estado: <span className="text-green-600 font-bold uppercase">{ticketData.estado}</span>
            </p>
          </div>
        </div>

        {/* REVERSO TÉRMINOS */}
        <div ref={reversoRef} className="bg-white rounded-3xl overflow-hidden shadow-xl p-6 relative">
          <div className="flex items-center gap-2 border-b pb-3 mb-3">
            <FaInfoCircle className="text-blue-600" />
            <h3 className="font-black text-slate-800 text-sm uppercase">Términos y Condiciones</h3>
          </div>

          <div className="space-y-4 text-[10px] text-slate-600 leading-relaxed text-justify">
            <div className="bg-blue-50 p-3 rounded-lg border border-blue-100">
              <p className="font-bold text-blue-700 uppercase mb-1 flex items-center gap-1">
                <FaMapMarkerAlt /> Punto de Abordaje
              </p>
              <p className="font-bold text-slate-800">Terminal Centro (PLAZA 522)</p>
              <p>Galeana 522, Zona Lunes Feb 09, Centro, 68000 Oaxaca de Juárez, Oax.</p>
            </div>

            <div>
              <p className="font-bold text-slate-900 uppercase mb-1">🕒 Abordaje y Validez</p>
              <ul className="list-disc pl-3 space-y-1">
                <li>El boleto es personal e intransferible. Válido solo para la fecha y hora marcada.</li>
                <li>Presentarse <strong className="text-slate-900">15 minutos antes</strong> de la salida.</li>
                <li>Obligatorio presentar identificación oficial vigente al abordar.</li>
                <li>Viaje con tranquilidad. Su boleto lo protege con seguro de viajero contra accidentes desde el momento en que aborda hasta llegar a su destino.</li>
              </ul>
            </div>

            <div>
              <p className="font-bold text-slate-900 uppercase mb-1">🧳 Política de Equipaje</p>
              <p>Derecho a transportar equipaje de uso personal. El exceso o mercancía generará costo extra. La empresa no se hace responsable por objetos de valor no reportados.</p>
            </div>

            <div>
              <p className="font-bold text-slate-900 uppercase mb-1">🔄 Cambios y Cancelaciones</p>
              <p className="text-red-500 font-bold">NO HAY REEMBOLSOS EN EFECTIVO PARA COMPRAS EN LÍNEA.</p>
              <p>Solo se permiten cambios de fecha con al menos 2 horas y media de anticipación, sujeto a disponibilidad y cargo del 10%.</p>
            </div>

            <div className="bg-red-50 p-2 rounded border border-red-100 text-red-700">
              <p className="font-bold text-[9px] text-center">🚫 PROHIBIDO ARMAS Y ALCOHOL</p>
              <p className="text-center mt-1">Nos reservamos el derecho de admisión a personas bajo efectos del alcohol y drogas.</p>
            </div>

            <div className="text-center pt-2 border-t mt-2">
              <p className="font-bold text-slate-400">Sugerencias: 954 167 7156</p>
            </div>
          </div>

          <div className="absolute top-0 right-0 w-20 h-20 bg-blue-500/5 rounded-bl-full pointer-events-none"></div>
        </div>

        {/* BOTONES */}
        <div className="flex flex-col gap-3">
          <button
            onClick={descargarImagenes}
            className="bg-green-500 hover:bg-green-600 text-white py-4 rounded-xl font-bold flex items-center justify-center gap-2 shadow-lg transition-transform active:scale-95"
          >
            <FaDownload /> Descargar Todo (Boleto + Info)
          </button>

          <button
            onClick={() => navigate('/')}
            className="bg-slate-700 hover:bg-slate-800 text-white py-4 rounded-xl font-bold flex items-center justify-center gap-2 transition-transform active:scale-95"
          >
            <FaHome /> Volver al Inicio
          </button>
        </div>

      </div>
    </div>
  );
}